// Cloudinary upload utility for direct frontend uploads
import { compressImageIfNeeded } from './imageCompression';

export interface CloudinaryUploadResponse {
  secure_url: string;
  public_id: string;
  format: string;
  width: number;
  height: number;
  bytes: number;
}

export const uploadToCloudinary = async (
  file: File,
  preset: string,
  folder: string = 'car-appraisals',
  customFileName?: string,
  onProgress?: (progress: number) => void
): Promise<CloudinaryUploadResponse> => {
  const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
  
  // Enhanced debug logging for mobile debugging
  console.log('Cloudinary Upload Debug:', {
    cloudName: cloudName ? 'Configured' : 'Missing',
    preset: preset ? 'Configured' : 'Missing',
    folder,
    fileName: file.name,
    customFileName,
    fileSize: file.size,
    fileType: file.type,
    userAgent: navigator.userAgent,
    isMobile: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
  });

  if (!cloudName) {
    throw new Error('Cloudinary cloud name not configured. Please check REACT_APP_CLOUDINARY_CLOUD_NAME in your .env file.');
  }

  if (!preset) {
    throw new Error('Upload preset not configured. Please check REACT_APP_CLOUDINARY_UPLOAD_PRESET in your .env file.');
  }

  // Validate file before upload
  if (!file || file.size === 0) {
    throw new Error('Invalid file: File is empty or corrupted');
  }

  // Compress image if it's too large (especially for mobile photos)
  let uploadFile = file;
  if (file.size > 5 * 1024 * 1024) { // 5MB threshold
    try {
      console.log('Compressing large image before upload...');
      uploadFile = await compressImageIfNeeded(file, 5);
      console.log('Image compressed successfully');
    } catch (compressionError) {
      console.warn('Image compression failed, uploading original:', compressionError);
      // Continue with original file if compression fails
    }
  }

  const formData = new FormData();
  formData.append('file', uploadFile);
  formData.append('upload_preset', preset);
  formData.append('folder', folder);
  
  // Use custom filename if provided
  if (customFileName) {
    formData.append('public_id', customFileName);
    console.log('Setting custom public_id:', customFileName);
  } else {
    console.log('No custom filename provided, using default');
  }

  const uploadUrl = `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`;
  console.log('Uploading to:', uploadUrl);

  try {
    // Add timeout for mobile connections
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 second timeout

    const response = await fetch(uploadUrl, {
      method: 'POST',
      body: formData,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    console.log('Upload response status:', response.status);
    console.log('Upload response headers:', Object.fromEntries(response.headers.entries()));

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Upload error response:', errorText);
      
      // Provide more specific error messages
      if (response.status === 413) {
        throw new Error('File too large. Please compress your image or choose a smaller file.');
      } else if (response.status === 400) {
        throw new Error('Invalid file format. Please use JPEG, PNG, or WebP images.');
      } else if (response.status === 401) {
        throw new Error('Upload authentication failed. Please check your Cloudinary configuration.');
      } else {
        throw new Error(`Upload failed: ${response.status} ${response.statusText} - ${errorText}`);
      }
    }

    const result = await response.json();
    console.log('Upload success:', result);
    return result;
  } catch (error) {
    console.error('Error uploading to Cloudinary:', error);
    
    // Handle specific error types
    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        throw new Error('Upload timed out. Please check your internet connection and try again.');
      } else if (error.message.includes('Failed to fetch')) {
        throw new Error('Network error. Please check your internet connection and try again.');
      }
    }
    
    throw error;
  }
};

export const uploadMultipleImages = async (
  files: { [key: string]: File | null },
  preset: string,
  customFileNamePrefix?: string,
  onProgress?: (key: string, progress: number) => void
): Promise<{ [key: string]: string }> => {
  const uploadPromises: Array<{ key: string; promise: Promise<string> }> = [];

  for (const [key, file] of Object.entries(files)) {
    if (file) {
      // Create custom filename with VIN prefix and photo type
      const customFileName = customFileNamePrefix 
        ? `${customFileNamePrefix}_${key}`
        : undefined;
      
      console.log(`Uploading ${key} with filename:`, customFileName);
      console.log(`File original name:`, file.name);
      
      uploadPromises.push({
        key,
        promise: uploadToCloudinary(file, preset, 'car-appraisals', customFileName).then(result => {
          console.log(`Upload completed for ${key}:`, result.public_id);
          return result.secure_url;
        })
      });
    }
  }

  // Track progress for each upload
  const results = await Promise.all(
    uploadPromises.map(async ({ key, promise }) => {
      try {
        // Simulate progress for better UX
        if (onProgress) {
          onProgress(key, 50); // Start at 50%
          setTimeout(() => onProgress(key, 100), 500); // Complete after 500ms
        }
        const url = await promise;
        return { key, url };
      } catch (error) {
        if (onProgress) {
          onProgress(key, 0); // Reset on error
        }
        throw error;
      }
    })
  );

  const uploadedUrls: { [key: string]: string } = {};
  results.forEach(({ key, url }) => {
    uploadedUrls[key] = url;
  });

  return uploadedUrls;
};

// Delete image from Cloudinary (for cleanup on failed submissions)
export const deleteFromCloudinary = async (publicId: string): Promise<boolean> => {
  const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
  
  if (!cloudName) {
    console.error('Cloudinary cloud name not configured');
    return false;
  }

  try {
    const response = await fetch(
      `https://api.cloudinary.com/v1_1/${cloudName}/image/destroy`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          public_id: publicId,
          api_key: process.env.REACT_APP_CLOUDINARY_API_KEY,
          signature: process.env.REACT_APP_CLOUDINARY_SIGNATURE,
        }),
      }
    );

    if (!response.ok) {
      console.error('Failed to delete image:', response.statusText);
      return false;
    }

    console.log('Image deleted successfully:', publicId);
    return true;
  } catch (error) {
    console.error('Error deleting image:', error);
    return false;
  }
};

// Cleanup uploaded images if submission fails
export const cleanupUploadedImages = async (uploadedUrls: { [key: string]: string }): Promise<void> => {
  console.log('Cleaning up uploaded images:', uploadedUrls);
  
  const deletePromises = Object.values(uploadedUrls).map(async (url) => {
    // Extract public_id from URL
    const urlParts = url.split('/');
    const filenameWithExt = urlParts[urlParts.length - 1];
    const publicId = filenameWithExt.split('.')[0]; // Remove file extension
    
    return deleteFromCloudinary(publicId);
  });

  await Promise.all(deletePromises);
  console.log('Cleanup completed');
}; 